import type { UpsertOwnerInput } from '@sea-snag/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { ownersApi } from '../lib/api-client';
import { queryKeys } from '../lib/query-keys';

export function useOwners(orgPid: string) {
  return useQuery({
    queryKey: queryKeys.owners.list(orgPid),
    queryFn: () => ownersApi.list(),
    enabled: !!orgPid,
  });
}

export function useOwnerMutations(orgPid: string) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: queryKeys.owners.list(orgPid) });

  const create = useMutation({
    mutationFn: (data: UpsertOwnerInput) => ownersApi.create(data),
    onSuccess: () => {
      void invalidate();
      toast.success('Owner created');
    },
    onError: () => toast.error('Failed to create owner'),
  });
  const update = useMutation({
    mutationFn: (args: { pid: string; data: UpsertOwnerInput }) =>
      ownersApi.update(args.pid, args.data),
    onSuccess: () => {
      void invalidate();
      toast.success('Owner updated');
    },
    onError: () => toast.error('Failed to update owner'),
  });
  const remove = useMutation({
    mutationFn: (pid: string) => ownersApi.remove(pid),
    onSuccess: () => {
      void invalidate();
      toast.success('Owner deleted');
    },
    onError: () => toast.error('Failed to delete owner'),
  });
  const regenerate = useMutation({
    mutationFn: (pid: string) => ownersApi.regenerate(pid),
    onSuccess: () => {
      void invalidate();
      toast.success('New link generated');
    },
    onError: () => toast.error('Failed to regenerate link'),
  });
  const revoke = useMutation({
    mutationFn: (pid: string) => ownersApi.revoke(pid),
    onSuccess: () => {
      void invalidate();
      toast.success('Link revoked');
    },
    onError: () => toast.error('Failed to revoke link'),
  });
  const sendEmail = useMutation({
    mutationFn: (pid: string) => ownersApi.sendEmail(pid),
    onSuccess: () => toast.success('Email sent'),
    onError: () => toast.error('Failed to send email'),
  });
  return { create, update, remove, regenerate, revoke, sendEmail };
}
